import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import FormInput from '../../shared/components/ui/FormInput';
import { Pencil, Trash, Copy } from 'lucide-react';
import {
  getAllSalesUsersByProjectId,
  getCampaignById,
  getCampaignLeads,
  getCampaignLeadsStats,
  bulkTransferCampaignLead
} from '../services/api/implementation/impl';
import { useMutation, useQuery } from '@tanstack/react-query';
import Loader from '../../shared/components/ui/Loader';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import toast from 'react-hot-toast';
import rightArrow from '../../shared/assets/svg/rightIcon.svg';
import noDataIcon from '../../shared/assets/png/noDataFound.png';
import { useParams } from 'react-router-dom';
import Button from '../../shared/components/ui/Button';
import Modal from '../../shared/components/ui/Modal';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { useSmartBack } from '../../shared/navigation';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useProjectsList } from '../../../contexts/ProjectsContext';

const CampaignOverView = () => {
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const { id, project_id, name } = useParams();
  const [isloading, setisLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState();
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { collapsed } = useSidebar();
  const { onBack } = useSmartBack({ fallback: '/builder/marketing/forms' });
  const {
    data: campaignLeadData,
    refetch: refetchCampaignLeadData,
    isLoading: isCampaignLoading
  } = useQuery({
    queryKey: ['getCampaignLeads', id],
    queryFn: () => getCampaignLeads(id),
    enabled: !!id
  });

  const {
    data: campaignLeadDataStats,
    refetch: refetchCampaignLeadStatsData,
    isLoading: isCampaignLeadStatsLoading
  } = useQuery({
    queryKey: ['getCampaignLeadsStats', id],
    queryFn: () => getCampaignLeadsStats(id),
    enabled: !!id
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Campaign Over View Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (id) {
      refetchCampaignLeadData();
      refetchCampaignLeadStatsData();
    }
  }, [id]);

  useEffect(() => {
    setisLoading(isCampaignLoading && isCampaignLeadStatsLoading);
  }, [isCampaignLoading, isCampaignLeadStatsLoading]);

  const validationSchema = yup.object().shape({
    project: yup.string().required('Select Project')
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
    watch
  } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const selectedSales = watch('sales_id');

  const { data: AllSalesGuy, refetch: fetchSales } = useQuery({
    queryKey: ['getAllSalesUsersByProjectId', project_id],
    queryFn: () => getAllSalesUsersByProjectId(project_id, 1, 500),
    enabled: !!project_id
  });

  useEffect(() => {
    if (project_id) fetchSales();
  }, [project_id, fetchSales]);

  const transformedDataSales =
    AllSalesGuy?.data
      ?.filter((item) => item.is_active)
      ?.map((item) => ({
        label: item.name || '',
        id: item.id,
        value: item.id
      })) || [];

  useEffect(() => {
    if (projects) {
      setValue('project', projects[0].id);
    }
  }, [projects]);

  const formatDate = (dateString) => {
    if (!dateString) return null;

    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' }); // May, Jun, etc.
    const year = date.getFullYear();

    // Add "st", "nd", "rd", "th"
    const getOrdinal = (n) => {
      if (n > 3 && n < 21) return 'th'; // 11th, 12th, 13th
      switch (n % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };

    return `${day}${getOrdinal(day)} ${month} ${year}`;
  };

  const isAllSelected =
    Array.isArray(campaignLeadData) &&
    campaignLeadData.length > 0 &&
    campaignLeadData.filter((lead) => !lead.isTransfered).length > 0 &&
    Array.isArray(selectedLeads) &&
    selectedLeads.length === campaignLeadData.filter((lead) => !lead.isTransfered).length;

  const handleSelectAll = () => {
    // Validate arrays before operations
    if (!Array.isArray(campaignLeadData) || campaignLeadData.length === 0) {
      console.warn('Campaign lead data is not a valid array or is empty');
      return;
    }

    if (!Array.isArray(selectedLeads)) {
      console.warn('Selected leads is not a valid array');
      setSelectedLeads([]);
      return;
    }

    const transferableLeads = campaignLeadData.filter((lead) => lead && !lead.isTransfered);

    if (transferableLeads.length === 0) {
      console.warn('No transferable leads available');
      setSelectedLeads([]);
      return;
    }

    if (isAllSelected) {
      setSelectedLeads([]);
    } else {
      const leadIds = transferableLeads
        .map((lead) => lead?.id)
        .filter((id) => id !== undefined && id !== null);

      setSelectedLeads(leadIds);
    }
  };

  const handleSelectLead = (leadId) => {
    const lead = campaignLeadData.find((l) => l.id === leadId);
    if (lead && lead.isTransfered) return; // Prevent selecting transferred leads
    setSelectedLeads((prev) =>
      prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]
    );
  };

  const handleTransfer = () => {
    if (!selectedSales || selectedSales === '') {
      toast.error('Select Sales Guy');
      return;
    }
    // Use selectedLeads if any, otherwise fallback to selectedLead
    const ids = selectedLeads.length > 0 ? selectedLeads : selectedLead ? [selectedLead] : [];
    if (ids.length === 0) {
      toast.error('No leads selected');
      return;
    }
    const payload = { ids, sales_id: Number(selectedSales) };
    handleTransferLead(payload);
  };

  const { mutate: handleTransferLead } = useMutation({
    mutationFn: (payload) => {
      // Return the promise so onSuccess runs after completion
      return bulkTransferCampaignLead(payload);
    },
    onMutate: () => {
      setIsSubmitting(true);
      setisLoading(true);
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      setisLoading(false);
      setModalOpen(false);
      setSelectedLead();
      toast.success('Lead transferred successfully');
      await refetchCampaignLeadData();
      await refetchCampaignLeadStatsData();
      setSelectedLeads([]);
    },
    onError: (err) => {
      setIsSubmitting(false);
      setisLoading(false);
      console.error('Error trasfering lead:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error trasfering lead');
    }
  });

  return (
    <>
      <Loader isVisible={isloading} />

      {modalOpen && (
        <Modal
          isDirty={true}
          width={30}
          name={'Transfer Lead'}
          onClose={() => {
            setModalOpen(false);
          }}>
          <div>
            <FormInput
              width={48}
              label={'Sales Guy'}
              register={register}
              register_key={'sales_id'}
              isRequired={true}
              type={'dropdown'}
              options={[
                { label: 'Select sales guy', value: '', disabled: true, selected: true },
                ...transformedDataSales
              ]}
            />
          </div>
          <div className="flex gap-2 mt-5 justify-end">
            <Button
              customStyle={{ width: '20%' }}
              displayType={'secondary'}
              onClick={() => {
                setModalOpen(false);
              }}>
              Close
            </Button>
            <Button customStyle={{ width: '20%' }} disabled={isSubmitting} onClick={handleTransfer}>
              Submit
            </Button>
          </div>
        </Modal>
      )}
      <Layout>
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header
            backArrow={true}
            onBack={onBack}
            data={{
              name: (
                <span className="flex items-center gap-1">
                  Back
                  <img src={rightArrow} alt="Back Arrow" className="w-4 h-4" />
                  {name || 'Campaign Name'}
                </span>
              )
            }}
          />
        </div>

        <div className="mt-[4.4rem]">
          <div
            className={`flex flex-col justify-end items-start gap-2 fixed  z-30 border  bg-white left-[18vw] px-6 py-7 transition-all duration-300 ease-in-out   ${
              collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
            } mt-[-0.40rem]`}>
            {/* <form className="flex justify-between items-end w-[100%]  ">
              <div className="flex justify-between items-center w-[50%] ">
                <FormInput
                  width={48}
                  label={'Project'}
                  register={register}
                  register_key={'project'}
                  isRequired={true}
                  type={'dropdown'}
                  options={transformedDataProjects}
                  validation={{ required: 'Project is required' }}
                  errors={errors}
                />
              </div>
            </form> */}
            <div className="flex gap-5">
              <div className="rounded-lg flex flex-col  border border-#DCDCDC p-3">
                <label className="font-semibold" htmlFor="visited">
                  People Visited
                </label>
                <p className="text-[#2A565E] font-bold text-[1.2rem]">
                  {campaignLeadDataStats?.totalVisitor}
                </p>
              </div>
              <div className="rounded-lg flex flex-col  border border-#DCDCDC p-3">
                <label className="font-semibold" htmlFor="visited">
                  People Submitted
                </label>
                <p className="text-[#2A565E] font-bold text-[1.2rem]">
                  {campaignLeadDataStats?.totalLeads}
                </p>
              </div>
              <div className="rounded-lg flex flex-col  border border-#DCDCDC p-3">
                <label className="font-semibold" htmlFor="visited">
                  Cost Per Lead
                </label>
                <p className="text-[#2A565E] font-bold text-[1.2rem]">
                  ₹{' '}
                  {campaignLeadDataStats?.cost_per_lead
                    ? Math.round(campaignLeadDataStats.cost_per_lead)
                    : '---'}
                </p>
              </div>
              <div className="rounded-lg flex flex-col  border border-#DCDCDC p-3">
                <label className="font-semibold" htmlFor="visited">
                  Top 5 Locations
                </label>
                <div className="flex">
                  {campaignLeadDataStats?.topLocations?.length > 0 ? (
                    <p className="text-[#2A565E] font-bold text-[1.2rem]">
                      {campaignLeadDataStats?.topLocations?.map((item) => item.location).join(', ')}
                    </p>
                  ) : (
                    <p className="text-[#2A565E] font-bold text-[1.2rem]">---</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="pt-[9.5rem] pl-4 pr-4 ">
            <div className="shadow overflow-auto rounded-lg h-[calc(100vh-12.5rem)]">
              {campaignLeadData && campaignLeadData.length > 0 ? (
                <table className="min-w-full rounded-t-xl text-left">
                  <thead className=" bg-gray-200 !text-gray-600 sticky top-0 z-20">
                    <tr className="font-normal h-[3rem]">
                      {/* <th className="px-2 py-2 border-b font-normal">
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                            aria-label="Select All"
                          />
                        </th> */}
                      <th className="px-3 py-2 border-b font-normal ">Name</th>
                      <th className="px-3 py-2 border-b font-normal">Email</th>
                      <th className="px-3 py-2 border-b font-normal">Contact</th>
                      <th className="px-3 py-2 border-b font-normal">Located</th>
                      <th className="px-3 py-2 border-b font-normal">Submission Date</th>
                      <th className="px-3 py-2 border-b font-normal">Repeated</th>
                      {/* <th className="px-3 py-2 border-b font-normal">Action</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {campaignLeadData.map((lead, index) => {
                      const isChecked = selectedLeads.includes(lead.id);
                      return (
                        <React.Fragment key={lead.id}>
                          <tr className="border-b hover:bg-gray-50 bg-white h-[3.5rem]">
                            {/* <td className="px-2 py-2">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleSelectLead(lead.id)}
                                  disabled={lead.isTransfered}
                                />
                              </td> */}
                            <td className="px-4 py-2">{lead.name}</td>
                            <td className="px-4 py-2">{lead.email || '--'}</td>
                            <td className="px-4 py-2">{lead.phone_number || '--'}</td>
                            <td className="px-4 py-2">{lead.location || '--'}</td>
                            <td className="px-4 py-2">{formatDate(lead.created_at) || '--'}</td>
                            <td className="px-4 py-2">{lead.repeated}</td>
                            {/* <td className="px-4 py-2">
                                {!lead.isTransfered && (
                                  <Button
                                    onClick={() => {
                                      setModalOpen(true);
                                      // If multi-select, don't override selectedLeads
                                      if (selectedLeads.length === 0) setSelectedLead(lead.id);
                                    }}>
                                    Transfer
                                  </Button>
                                )}
                              </td> */}
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="flex justify-center items-center h-[30rem] text-gray-600">
                  <img
                    src={noDataIcon}
                    alt="No Data"
                    className="w-[20rem] h-[15rem] object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default CampaignOverView;
